import { useCallback, useEffect, useState } from 'react';
import { api, ErrorApi, fetchAutenticado } from '../api/cliente';
import type {
  AccionCompra, OrdenCompra, OrdenCompraDetalle, Opcion, Pagina,
  Refaccion, ResumenCompras, SolicitudCompra,
} from '../api/tipos';
import { useSesion } from '../sesion';
import { folioOT } from '../folio';
import {
  Cargando, Dialogo, DialogoMotivo, Error, Falla, Faltantes, motivoDeFalla,
  Paginacion,
} from '../componentes/comunes';
import ServiciosReparacion from './ServiciosReparacion';
// La ficha completa de la orden de trabajo, para abrirla desde su folio
// sin salir de Compras. Es la misma que usa el programa preventivo.
import { Ficha as FichaDeLaOrden } from './Ordenes';
import { imprimirCompra } from './imprimirCompra';
import { aligerar } from '../imagenes';

/**
 * Compras: solicitudes y órdenes de compra.
 *
 * El circuito es: alguien pide una pieza que no hay, Compras la agrupa
 * con su proveedor y precio, alguien autoriza el gasto, se envía y al
 * llegar la mercancía se recibe. La recepción es lo que alimenta el
 * inventario.
 */
export default function Compras() {
  const { puede } = useSesion();
  const [vista, setVista] = useState<'ordenes' | 'solicitudes' | 'servicios'>('ordenes');
  const [resumen, setResumen] = useState<ResumenCompras | null>(null);
  const [recarga, setRecarga] = useState(0);
  // La solicitud desde la que se está armando una compra. Se abre en la
  // pestaña de solicitudes y se termina en la de órdenes: el camino
  // natural es ver lo que alguien pidió y comprarlo, no memorizar el
  // folio y volver a buscarlo en otra pantalla.
  const [comprando, setComprando] = useState<SolicitudCompra | null>(null);

  useEffect(() => {
    api.get<ResumenCompras>('/ordenes-compra/resumen').then(setResumen).catch(() => undefined);
  }, [recarga]);

  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Compras</h1>
          <p className="sub">
            Lo que hace falta, a quién se le compra y cuándo llegó. La
            recepción es lo que da entrada al inventario.
          </p>
        </div>
      </div>

      {resumen && (
        <div className="rejilla-catalogos">
          <Tarjeta titulo="Solicitudes sin atender" valor={resumen.solicitudesPendientes}
                   alerta={resumen.solicitudesPendientes > 0} />
          <Tarjeta titulo="Órdenes por autorizar" valor={resumen.porAutorizar}
                   alerta={resumen.porAutorizar > 0} />
          <Tarjeta titulo="Órdenes por recibir" valor={resumen.porRecibir} />
        </div>
      )}

      <nav className="submenu">
        <button
          className={vista === 'ordenes' ? 'activo' : ''}
          onClick={() => setVista('ordenes')}
        >
          Órdenes de compra
        </button>
        <button
          className={vista === 'solicitudes' ? 'activo' : ''}
          onClick={() => setVista('solicitudes')}
        >
          Solicitudes
        </button>
        {/* El catálogo que alimenta la cotización del técnico dentro de la
            orden. Vive aquí porque Compras es quien negocia con ellos. */}
        <button
          className={vista === 'servicios' ? 'activo' : ''}
          onClick={() => setVista('servicios')}
        >
          Servicios de reparación
        </button>
      </nav>

      {vista === 'ordenes' && (
        <Ordenes
          puede={puede} recarga={recarga}
          alCambiar={() => setRecarga((n) => n + 1)}
          inicial={comprando}
          alUsarInicial={() => setComprando(null)}
        />
      )}

      {vista === 'solicitudes' && (
        <Solicitudes
          puede={puede} recarga={recarga}
          alCambiar={() => setRecarga((n) => n + 1)}
          alComprar={(s) => { setComprando(s); setVista('ordenes'); }}
        />
      )}

      {vista === 'servicios' && <ServiciosReparacion puede={puede} />}
    </>
  );
}

function Tarjeta({ titulo, valor, alerta }: { titulo: string; valor: number; alerta?: boolean }) {
  return (
    <div className="tarjeta" style={{ cursor: 'default' }}>
      <div className="conteo">{titulo}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: alerta ? '#b02a37' : undefined }}>
        {valor}
      </div>
    </div>
  );
}

function moneda(valor: number): string {
  return valor.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
}

function fechaCorta(iso: string): string {
  return new Date(iso).toLocaleDateString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

const ETIQUETAS: Record<string, string> = {
  borrador: 'Borrador',
  pendiente_autorizacion: 'Por autorizar',
  autorizada: 'Autorizada',
  rechazada: 'Rechazada',
  enviada_proveedor: 'Con el proveedor',
  recibida_parcial: 'Recibida parcial',
  recibida_total: 'Recibida',
  pagada: 'Pagada',
  cancelada: 'Cancelada',
  creada: 'Pendiente',
  en_oc: 'En orden de compra',
  atendida: 'Atendida',
};

/**
 * Dónde está la mercancía.
 *
 * El estatus de la orden de compra cuenta eso y sólo eso: si ya se envió,
 * si llegó, si llegó a medias. Que esté pagada o no vive en su propia
 * columna, porque una compra pagada que sigue con el proveedor y una que
 * ya está en el anaquel son situaciones distintas y las dos importan.
 */
const ETIQUETAS_MERCANCIA: Record<string, string> = {
  borrador: 'Borrador',
  pendiente_autorizacion: 'Por autorizar',
  autorizada: 'Por enviar',
  rechazada: 'Rechazada',
  enviada_proveedor: 'En camino',
  recibida_parcial: 'Llegó a medias',
  recibida_total: 'Recibida',
  pagada: 'Cerrada',
  cancelada: 'Cancelada',
};

/** Y dónde está el dinero, en corto para que quepa en la columna. */
const ETIQUETAS_PAGO_CORTO: Record<string, string> = {
  pendiente_autorizacion: 'Sin autorizar',
  autorizado: 'Por pagar',
  pagado: 'Pagado',
  procedente_sin_pago: 'Sin pago',
  rechazado: 'Rechazado',
};

/** En qué va el egreso al proveedor, visto desde Compras. */
const ETIQUETAS_PAGO: Record<string, string> = {
  pendiente_autorizacion: 'Solicitado, sin autorizar',
  autorizado: 'Autorizado, por pagar',
  pagado: 'Pagado',
  procedente_sin_pago: 'Cerrado sin pago',
  rechazado: 'Rechazado',
};

/**
 * La prioridad de una compra.
 *
 * La pone Compras al crear y la leen quien autoriza y contabilidad: con
 * veinte órdenes en la bandeja, saber cuál corre es la diferencia entre
 * atender por orden de llegada y atender por lo que urge. La lista viene
 * ya ordenada por ella.
 */
const ETIQUETAS_PRIORIDAD: Record<string, { texto: string; clase: string }> = {
  urgente: { texto: 'Urgente', clase: 'no' },
  alta: { texto: 'Alta', clase: 'no' },
  media: { texto: 'Media', clase: '' },
  baja: { texto: 'Baja', clase: '' },
};

export function Prioridad({ valor }: { valor: string }) {
  const p = ETIQUETAS_PRIORIDAD[valor] ?? { texto: valor, clase: '' };
  return <span className={`etiqueta ${p.clase}`}>{p.texto}</span>;
}

type Puede = (modulo: string, accion: string) => boolean;

// ══════════════════════════════════════════════════ órdenes de compra

function Ordenes({
  puede, recarga, alCambiar, inicial, alUsarInicial,
}: {
  puede: Puede; recarga: number; alCambiar: () => void;
  /** Solicitud con la que llega abierta la orden, si se vino de comprarla. */
  inicial?: SolicitudCompra | null;
  alUsarInicial?: () => void;
}) {
  const [bandeja, setBandeja] = useState('abiertas');
  const [busqueda, setBusqueda] = useState('');
  const [numero, setNumero] = useState(1);
  const [pagina, setPagina] = useState<Pagina<OrdenCompra> | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [abierta, setAbierta] = useState<string | null>(null);
  /**
   * La orden de trabajo detrás de la compra, abierta desde su folio.
   *
   * Sustituye a las columnas de zona y tienda: ahí está eso y todo lo
   * demás —el área, la falla, en qué va—, sin dos columnas que en las
   * compras de abasto venían siempre en blanco.
   */
  const [ordenAbierta, setOrdenAbierta] = useState<string | null>(null);
  const [creando, setCreando] = useState(false);

  const cargar = useCallback(() => {
    setCargando(true);
    setError(null);
    api.get<Pagina<OrdenCompra>>('/ordenes-compra', {
      bandeja: bandeja || undefined,
      busqueda: busqueda || undefined,
      pagina: numero,
      limite: 25,
    })
      .then(setPagina)
      .catch(setError)
      .finally(() => setCargando(false));
  }, [bandeja, busqueda, numero, recarga]);

  useEffect(() => {
    const t = setTimeout(cargar, busqueda ? 300 : 0);
    return () => clearTimeout(t);
  }, [cargar, busqueda]);

  // Si se llegó aquí desde una solicitud, la orden se abre sola con esa
  // pieza dentro.
  useEffect(() => {
    if (inicial) setCreando(true);
  }, [inicial]);

  return (
    <div className="panel">
      <div className="controles">
        <select value={bandeja} onChange={(e) => { setBandeja(e.target.value); setNumero(1); }}>
          <option value="abiertas">Abiertas</option>
          <option value="por_autorizar">Por autorizar</option>
          <option value="por_recibir">Por recibir</option>
          <option value="cerradas">Cerradas</option>
          {/* Las dos del dinero. Van juntas y separadas de las de arriba
              porque contestan otra pregunta: aquéllas dicen dónde está
              la mercancía, éstas dónde está el pago. */}
          <option value="por_pagar">Por pagar</option>
          <option value="pagadas">Pagadas</option>
          {/* Y la del crédito, que es la que impide que se pierda una
              deuda: el pago de estas compras no existe todavía, así que
              si nadie mira esta lista nadie va a extrañarlo. */}
          <option value="a_credito">A crédito, sin factura</option>
          <option value="">Todas</option>
        </select>
        <input
          type="search"
          placeholder="Folio, proveedor u orden de trabajo…"
          value={busqueda}
          onChange={(e) => { setBusqueda(e.target.value); setNumero(1); }}
        />
        {puede('compras', 'crear') && (
          <button className="primario" onClick={() => setCreando(true)}>Nueva orden</button>
        )}
      </div>

      <Error error={error} />

      {cargando && !pagina ? <Cargando /> : pagina && (
        <>
          {pagina.data.length === 0 ? (
            <div className="vacio">No hay órdenes de compra en esta bandeja.</div>
          ) : (
            <table>
              <thead>
                {/*
                  El orden lo puso Karin, y sigue cómo se lee una compra:
                  de qué es —folio, orden, proveedor, concepto—, cuánto
                  cuesta, dónde va, y en qué va. La prioridad al final: es
                  con lo que se ordena la lista, no lo que se busca al
                  recorrerla.

                  Cuatro columnas salieron de esta vista. El cargo es una
                  regla de la orden de trabajo, no de la compra; la entrega
                  estimada casi siempre venía vacía; y zona y tienda son
                  del pedido —en las de abasto venían las dos en blanco—.

                  Las tres primeras llegaron juntas en la 4de7b68, cuando
                  la lista sólo decía «3 partidas» y había que abrir cada
                  orden para saber qué se estaba pagando. Eso lo resolvió
                  el concepto; lo demás se quedó de más. Para no perder el
                  dato, el folio de la OT abre su ficha desde aquí.
                */}
                <tr>
                  <th>Folio</th>
                  <th>Orden</th>
                  <th>Proveedor</th>
                  <th>Concepto</th>
                  <th className="numero">Total</th>
                  {/* Dos columnas y no una: el pago cuenta dónde está el
                      dinero y el estatus dónde está la mercancía. Juntarlos
                      obligaba a elegir cuál de los dos mentir. */}
                  <th>Pago</th>
                  <th>Mercancía</th>
                  <th>Prioridad</th>
                </tr>
              </thead>
              <tbody>
                {pagina.data.map((o) => (
                  <tr key={o.id}>
                    <td className="mono">
                      <button className="enlace" onClick={() => setAbierta(o.id)}>{o.folio}</button>
                    </td>
                    {/* El folio abre la orden completa. Zona y tienda
                        salieron de la lista y viven ahí: de un clic en vez
                        de dos columnas que en las compras de abasto venían
                        siempre en blanco. */}
                    <td className="mono">
                      {o.ordenTrabajo ? (
                        <button
                          className="enlace"
                          onClick={() => setOrdenAbierta(o.ordenTrabajo!.id)}
                        >
                          {folioOT(o.ordenTrabajo.folio)}
                        </button>
                      ) : o.anticipada ? (
                        /* Karin: «falta dejar claro a gerencia que es de
                           una solicitud anticipada, por eso no trae
                           orden». El guion se leía como un dato que
                           falta, y quien autoriza el gasto se quedaba sin
                           saber para qué es. */
                        <>
                          <div className="ayuda">Anticipada</div>
                          <div>{o.anticipada.folio}</div>
                        </>
                      ) : '—'}
                    </td>
                    <td>{o.proveedor?.nombre ?? '—'}</td>
                    {/* De qué es. Antes decía «3 partidas», que obliga a
                        abrir cada orden para saber qué se está pagando. */}
                    <td>
                      {o.concepto ?? '—'}
                      {o.numeroPartidas > 1 && (
                        <span className="conteo"> · {o.numeroPartidas} partidas</span>
                      )}
                    </td>
                    <td className="numero">{moneda(o.total)}</td>
                    <td>
                      {/*
                        La de crédito sin factura no tiene pago, y el
                        guion se leía como «no se sabe». Lo que falta es
                        la factura, y decirlo aquí es lo que distingue
                        una deuda que espera papel de una que espera
                        dinero.
                      */}
                      {o.aCredito && !o.factura
                        ? (
                          <span className={`etiqueta ${o.esperaFactura?.seTardo ? 'no' : ''}`}>
                            Espera factura
                            {/* Y cuánto lleva. Sin esto, la de hace dos
                                meses se veía igual que la de ayer, y la
                                que se pierde es la vieja. */}
                            {o.esperaFactura ? ` · ${o.esperaFactura.dias} d` : ''}
                          </span>
                        )
                        : o.estatusPago
                          ? (
                            <span className={`etiqueta ${o.estatusPago === 'pagado' ? 'si' : o.estatusPago === 'rechazado' ? 'no' : ''}`}>
                              {ETIQUETAS_PAGO_CORTO[o.estatusPago] ?? o.estatusPago}
                            </span>
                          )
                          : <span className="ayuda">—</span>}
                    </td>
                    <td>
                      <span className={`etiqueta ${o.estatus === 'cancelada' || o.estatus === 'rechazada' ? 'no' : o.estatus === 'pagada' || o.estatus === 'recibida_total' ? 'si' : ''}`}>
                        {ETIQUETAS_MERCANCIA[o.estatus] ?? ETIQUETAS[o.estatus] ?? o.estatus}
                      </span>
                    </td>
                    <td><Prioridad valor={o.prioridad} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <Paginacion
            pagina={pagina.pagina} limite={pagina.limite} total={pagina.total}
            onCambiar={setNumero}
          />
        </>
      )}

      {/*
        La orden de trabajo, abierta desde el folio de la lista.

        `tecnicos` va vacío a propósito: la ficha carga sola los de la
        zona de la orden y ese arreglo sólo es el respaldo mientras llega.
        Compras no reparte técnicos —ni tiene el permiso para hacerlo— así
        que traer la lista aquí sería una consulta que nadie usa.
      */}
      {ordenAbierta && (
        <FichaDeLaOrden
          id={ordenAbierta}
          tecnicos={[]}
          onCerrar={() => setOrdenAbierta(null)}
          onCambio={() => cargar()}
        />
      )}

      {abierta && (
        <FichaOrden
          id={abierta}
          onCerrar={() => setAbierta(null)}
          onCambio={() => { cargar(); alCambiar(); }}
        />
      )}

      {creando && (
        <FormularioOrden
          inicial={inicial}
          onCerrar={() => { setCreando(false); alUsarInicial?.(); }}
          onCreada={(id) => {
            setCreando(false);
            alUsarInicial?.();
            cargar();
            alCambiar();
            setAbierta(id);
          }}
        />
      )}
    </div>
  );
}

export function FichaOrden({
  id, onCerrar, onCambio,
}: {
  id: string; onCerrar: () => void; onCambio: () => void;
}) {
  const [orden, setOrden] = useState<OrdenCompraDetalle | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [accion, setAccion] = useState<AccionCompra | null>(null);
  const [recibiendo, setRecibiendo] = useState(false);
  const [pidiendoPago, setPidiendoPago] = useState(false);
  const [facturando, setFacturando] = useState(false);
  const [quitando, setQuitando] = useState(false);
  const { puede } = useSesion();

  const cargar = useCallback(() => {
    api.get<OrdenCompraDetalle>(`/ordenes-compra/${id}`).then(setOrden).catch(setError);
  }, [id]);

  useEffect(cargar, [cargar]);

  if (!orden) {
    return (
      <Dialogo titulo="Orden de compra" onCerrar={onCerrar}
               pie={<button onClick={onCerrar}>Cerrar</button>}>
        <Error error={error} />
        {!error && <Cargando />}
      </Dialogo>
    );
  }

  const porRecibir = orden.estatus === 'enviada_proveedor' || orden.estatus === 'recibida_parcial';
  const recibida = orden.estatus === 'recibida_parcial' || orden.estatus === 'recibida_total';

  function pedirPago() {
    setError(null);
    setPidiendoPago(true);
    api.post(`/ordenes-compra/${id}/pedir-pago`)
      .then(() => { cargar(); onCambio(); })
      .catch(setError)
      .finally(() => setPidiendoPago(false));
  }

  return (
    <>
      <Dialogo
        titulo={`${orden.folio} · ${ETIQUETAS[orden.estatus] ?? orden.estatus}`}
        onCerrar={onCerrar}
        pie={<button onClick={onCerrar}>Cerrar</button>}
      >
        <Error error={error} />

        {orden.motivoRechazo && (
          <div className="aviso error">Rechazada: {orden.motivoRechazo}</div>
        )}

        <Dato etiqueta="Proveedor" valor={orden.proveedor?.nombre ?? null} />
        <Dato
          etiqueta="Orden de trabajo"
          valor={orden.ordenTrabajo ? folioOT(orden.ordenTrabajo.folio) : null}
        />
        {/* Sólo cuando no hay orden y sí hay anticipada: es lo que
            convierte el hueco en una explicación. */}
        {!orden.ordenTrabajo && orden.anticipada && (
          <Dato
            etiqueta="Viene de una solicitud anticipada"
            valor={`${orden.anticipada.folio}${orden.anticipada.sucursal
              ? ` · ${orden.anticipada.sucursal.determinante} — ${orden.anticipada.sucursal.nombre}`
              : ''}`}
          />
        )}
        <Dato etiqueta="Tipo" valor={orden.tipo === 'ot' ? 'Para una orden' : 'Abasto'} />
        <Dato
          etiqueta="Entrega estimada"
          valor={orden.fechaEntregaEstimada ? fechaCorta(orden.fechaEntregaEstimada) : null}
        />
        {orden.observaciones && <Dato etiqueta="Observaciones" valor={orden.observaciones} />}

        {/*
          La compra a crédito, y su factura.

          Karin, del caso de RATIONAL: «cuando el trabajo se termina
          rational le manda a aprisa la factura para pago. aqui se me
          ocurre que compras suba la factura para que contabilidad la
          pueda pagar».

          Va antes del pago porque es lo que lo abre: sin factura no hay
          pago que mirar debajo, y el orden lo dice sin explicarlo.
        */}
        {orden.aCredito && (
          <div className="aviso" style={{ marginTop: 12 }}>
            <strong>
              Compra a crédito{orden.diasCredito ? ` · ${orden.diasCredito} días` : ''}.
            </strong>
            {orden.factura ? (
              <>
                <p style={{ margin: '6px 0 0' }}>
                  Factura <strong>{orden.factura.folio}</strong> del{' '}
                  {fechaCorta(orden.factura.fecha)} por{' '}
                  <strong>{moneda(orden.factura.importe)}</strong>. Su pago ya
                  está en la bandeja de Contabilidad.
                </p>
                <div className="controles" style={{ marginTop: 8 }}>
                  <button
                    className="enlace"
                    onClick={() => {
                      void api.descargar(
                        `/ordenes-compra/${orden.id}/factura/archivo`,
                        undefined,
                        `Factura ${orden.factura?.folio ?? ''}`.trim(),
                      ).catch(setError);
                    }}
                  >
                    Ver la factura
                  </button>
                  {/*
                    La salida de la que se capturó mal.

                    Sin esto, un folio o un importe mal tecleados dejaban
                    la compra con ese pago y sin vuelta atrás —y el
                    mensaje de «ya tiene su factura» prometía este paso—.
                    La API se niega si el pago ya salió: ahí lo que
                    corresponde es una nota de crédito.
                  */}
                  {puede('compras', 'editar') && (
                    <button className="enlace" onClick={() => setQuitando(true)}>
                      Quitarla
                    </button>
                  )}
                </div>
              </>
            ) : (
              <>
                <p style={{ margin: '6px 0 0' }}>
                  El proveedor surte y factura después, así que esta compra
                  todavía no tiene pago pedido: no se le debe nada hasta que
                  llegue su factura. La pieza se recibe y se surte igual.
                </p>
                {puede('compras', 'editar') && (
                  <div className="controles" style={{ marginTop: 8 }}>
                    <button className="primario" onClick={() => setFacturando(true)}>
                      Registrar la factura
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* En qué va el dinero. Compras no ejecuta el pago, pero
            preguntarle a Contabilidad si ya salió era media hora de todos
            los días. */}
        {orden.pago && (
          <>
            <Dato
              etiqueta="Pago al proveedor"
              valor={
                orden.pago.estatus === 'pagado'
                  ? `Pagado${orden.pago.referencia ? ` · ${orden.pago.referencia}` : ''}`
                  : ETIQUETAS_PAGO[orden.pago.estatus] ?? orden.pago.estatus
              }
            />

            {/* El comprobante lo sube Contabilidad y lo ocupa Compras: es
                lo que se le manda al proveedor para que suelte la
                mercancía. Tenerlo sólo en la ficha del pago obligaba a
                pedirlo por chat, y lo que se pide por chat se pierde. */}
            {orden.pago.comprobantes.length > 0 && (
              <div className="campo">
                <label>Comprobante del pago</label>
                <ul style={{ listStyle: 'none', display: 'grid', gap: 4, margin: 0, padding: 0 }}>
                  {orden.pago.comprobantes.map((c) => (
                    <li key={c.id}>
                      <button
                        className="enlace mono"
                        onClick={() => {
                          void api.descargar(
                            `/ordenes-compra/${orden.id}/comprobantes/${c.id}`,
                            undefined,
                            c.nombre,
                          ).catch(setError);
                        }}
                      >
                        {c.nombre}
                      </button>
                    </li>
                  ))}
                </ul>
                <span className="ayuda">
                  Descárgalo para mandárselo al proveedor. Después márcala como
                  enviada.
                </span>
              </div>
            )}
          </>
        )}

        <h2>Partidas</h2>
        <table>
          <thead>
            <tr>
              <th>Refacción</th>
              <th className="numero">Cantidad</th>
              <th className="numero">Recibido</th>
              <th className="numero">Unitario</th>
              <th className="numero">Importe</th>
            </tr>
          </thead>
          <tbody>
            {orden.partidas.map((p) => (
              <tr key={p.id}>
                <td>
                  {p.refaccion
                    ? <><span className="mono">{p.refaccion.codigo}</span> {p.refaccion.nombre}</>
                    : p.descripcionLibre}
                </td>
                <td className="numero">{p.cantidad}</td>
                <td className="numero">
                  {p.cantidadRecibida}
                  {p.pendiente > 0 && orden.estatus !== 'borrador' && (
                    <span className="etiqueta no" style={{ marginLeft: 6 }}>
                      faltan {p.pendiente}
                    </span>
                  )}
                </td>
                <td className="numero">{moneda(p.montoCompra)}</td>
                <td className="numero">{moneda(p.importe)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="aviso">
          <div style={{ display: 'grid', gap: 4 }}>
            <span>Subtotal: <strong>{moneda(orden.subtotal)}</strong></span>
            <span>
              IVA: <strong>{moneda(orden.iva)}</strong>
            </span>
            {orden.retencion > 0 && (
              <span>Retención: <strong>−{moneda(orden.retencion)}</strong></span>
            )}
            <span style={{ fontSize: 16 }}>Total: <strong>{moneda(orden.total)}</strong></span>
          </div>
        </div>

        <div className="controles">
          {/* Imprimir no depende del estado: un borrador se imprime para
              revisarlo antes de mandarlo a autorizar, y una orden cerrada
              se reimprime cuando el proveedor pide copia. */}
          <button onClick={() => imprimirCompra(orden)}>Imprimir</button>
          {orden.acciones.map((a) => (
            <button
              key={a.a}
              className={a.a === 'cancelada' || a.a === 'rechazada' ? 'peligro' : 'primario'}
              onClick={() => setAccion(a)}
              title={a.explicacion}
            >
              {a.etiqueta}
            </button>
          ))}
          {/* Compras no recibe. Quien tiene la mercancía enfrente es
              almacén, y es quien da la entrada al inventario: separar las
              dos manos es lo que impide que quien negoció la compra
              confirme una llegada que no ocurrió. La API ya lo exigía
              —recibir pide permiso de almacén— pero el botón vivía aquí,
              donde almacén no entra. */}
          {porRecibir && (
            <p className="ayuda">
              La recepción la registra Almacén cuando llegue la mercancía.
            </p>
          )}

          {/* La salida de la orden que se quedó atorada.
              Recibida y sin pago no tiene camino: no llega a «pagada»
              porque no hay pago que se ejecute, y no se cancela porque la
              pieza ya está en el anaquel. Recibir ahora abre el pago solo;
              esto es para las que se recibieron antes de eso. */}
          {/*
            Y no en las de crédito: ahí la falta de pago no es un hueco,
            es la regla. Su pago lo abre la factura, y este botón lo
            abriría por el total de la orden y sin el papel que lo
            sostiene —justo lo que la 0078 vino a evitar—.
          */}
          {recibida && orden.pago === null && !orden.aCredito
            && puede('compras', 'editar') && (
            <button className="primario" disabled={pidiendoPago} onClick={pedirPago}>
              Pedir el pago que falta
            </button>
          )}
        </div>

        {recibida && orden.pago === null && !orden.aCredito && (
          <div className="aviso" style={{ marginTop: 12 }}>
            Esta compra está recibida y <strong>no tiene pago</strong> en
            Contabilidad, así que se va a quedar abierta para siempre: no
            puede llegar a «pagada» —no hay pago que ejecutar— ni cancelarse
            —la pieza ya entró al inventario—. Pídelo y Contabilidad decide:
            lo paga, o lo cierra como procedente sin pago si no hubo salida
            de dinero.
          </div>
        )}
      </Dialogo>

      {accion && (
        <TransicionOrden
          ordenId={id}
          accion={accion}
          onCerrar={() => setAccion(null)}
          onHecho={() => { setAccion(null); cargar(); onCambio(); }}
        />
      )}

      {quitando && (
        <QuitarFactura
          ordenId={id}
          folio={orden.factura?.folio ?? ''}
          onCerrar={() => setQuitando(false)}
          onHecho={() => { setQuitando(false); cargar(); onCambio(); }}
        />
      )}

      {facturando && (
        <RegistrarFactura
          orden={orden}
          onCerrar={() => setFacturando(false)}
          onHecho={() => { setFacturando(false); cargar(); onCambio(); }}
        />
      )}

      {recibiendo && (
        <Recepcion
          orden={orden}
          onCerrar={() => setRecibiendo(false)}
          onHecho={() => { setRecibiendo(false); cargar(); onCambio(); }}
        />
      )}
    </>
  );
}

function TransicionOrden({
  ordenId, accion, onCerrar, onHecho,
}: {
  ordenId: string; accion: AccionCompra; onCerrar: () => void; onHecho: () => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);
  const pideMotivo = accion.requiere?.includes('motivo') ?? false;

  async function confirmar() {
    setError(null);
    setTrabajando(true);
    try {
      await api.post(`/ordenes-compra/${ordenId}/transicion`, {
        hacia: accion.a,
        motivo: motivo.trim() || undefined,
      });
      onHecho();
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  if (!pideMotivo) {
    return (
      <Dialogo
        titulo={accion.etiqueta}
        onCerrar={onCerrar}
        pie={
          <>
            <button onClick={onCerrar} disabled={trabajando}>Cancelar</button>
            <button className="primario" onClick={confirmar} disabled={trabajando}>
              {trabajando ? 'Guardando…' : accion.etiqueta}
            </button>
          </>
        }
      >
        <Error error={error} />
        <p>{accion.explicacion}</p>
      </Dialogo>
    );
  }

  return (
    <DialogoMotivo
      titulo={accion.etiqueta}
      textoBoton={accion.etiqueta}
      motivo={motivo}
      setMotivo={setMotivo}
      trabajando={trabajando}
      error={error}
      onConfirmar={confirmar}
      onCerrar={onCerrar}
      descripcion={<p>{accion.explicacion}</p>}
    />
  );
}

/**
 * Quitar la factura que se capturó mal.
 *
 * Pide motivo, como toda marcha atrás del sistema, y usa el mismo
 * diálogo que las transiciones: quitar una factura es rechazar un pago,
 * y debe verse tan serio como cancelar una compra.
 */
function QuitarFactura({ ordenId, folio, onCerrar, onHecho }: {
  ordenId: string;
  folio: string;
  onCerrar: () => void;
  onHecho: () => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function confirmar() {
    setError(null);
    setTrabajando(true);
    try {
      await api.post(`/ordenes-compra/${ordenId}/factura/quitar`, { motivo });
      onHecho();
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  return (
    <DialogoMotivo
      titulo={`Quitar la factura ${folio}`}
      textoBoton="Quitarla"
      motivo={motivo}
      setMotivo={setMotivo}
      trabajando={trabajando}
      error={error}
      onConfirmar={confirmar}
      onCerrar={onCerrar}
      descripcion={(
        <p>
          Se rechaza el pago que abrió y la compra vuelve a quedar esperando
          factura, lista para capturar la correcta. El archivo se conserva.
          Si el pago ya se ejecutó, esto no procede: ahí lo que corresponde
          es una nota de crédito del proveedor.
        </p>
      )}
    />
  );
}

/**
 * Registrar la factura que el proveedor mandó para cobrar.
 *
 * Karin, del caso de RATIONAL: «aqui se me ocurre que compras suba la
 * factura para que contabilidad la pueda pagar».
 *
 * Guardar aquí no es adjuntar un archivo: abre el pago, por el importe
 * de la factura. Por eso se pide el importe y no se da por supuesto el
 * total de la orden —un flete o un ajuste de precio son legítimos— y
 * por eso la diferencia, si la hay, se enseña antes de guardar.
 */
function RegistrarFactura({ orden, onCerrar, onHecho }: {
  orden: OrdenCompraDetalle;
  onCerrar: () => void;
  onHecho: () => void;
}) {
  const [folio, setFolio] = useState('');
  const [uuidFiscal, setUuidFiscal] = useState('');
  // La de hoy por omisión, que es el caso normal: llega la factura y se
  // captura. Si es de hace dos semanas, se corrige, y el vencimiento
  // sale de ahí.
  const [fecha, setFecha] = useState(() => new Date().toISOString().slice(0, 10));
  const [importe, setImporte] = useState(String(orden.total));
  const [archivo, setArchivo] = useState<File | null>(null);
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const cuanto = Number(importe) || 0;
  const diferencia = Math.round((cuanto - orden.total) * 100) / 100;

  const faltantes: string[] = [];
  if (!folio.trim()) faltantes.push('capturar el folio de la factura');
  if (!fecha) faltantes.push('poner la fecha de la factura');
  if (cuanto <= 0) faltantes.push('capturar el importe');
  if (!archivo) faltantes.push('adjuntar el PDF o la foto de la factura');

  async function guardar() {
    if (!archivo) return;

    setError(null);
    setTrabajando(true);
    try {
      const cuerpo = new FormData();
      cuerpo.append('archivo', await aligerar(archivo));
      cuerpo.append('folio', folio.trim());
      if (uuidFiscal.trim()) cuerpo.append('uuidFiscal', uuidFiscal.trim());
      cuerpo.append('fecha', fecha);
      cuerpo.append('importe', String(cuanto));

      const respuesta = await fetchAutenticado(
        `/api/v1/ordenes-compra/${orden.id}/factura`,
        { method: 'POST', body: cuerpo },
      );

      if (!respuesta.ok) {
        const texto = await respuesta.text();
        const cuerpoError = texto ? (JSON.parse(texto) as Record<string, unknown>) : {};
        throw new ErrorApi(
          respuesta.status,
          String(cuerpoError.codigo_negocio ?? 'error'),
          String(cuerpoError.detail ?? 'No se pudo registrar la factura.'),
        );
      }

      onHecho();
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  return (
    <Dialogo
      titulo={`Factura de ${orden.proveedor?.nombre ?? 'el proveedor'}`}
      onCerrar={onCerrar}
      pie={(
        <>
          <button onClick={onCerrar} disabled={trabajando}>Cancelar</button>
          <button
            className="primario"
            onClick={guardar}
            disabled={faltantes.length > 0 || trabajando}
          >
            {trabajando ? 'Guardando…' : 'Registrar y pedir el pago'}
          </button>
        </>
      )}
    >
      <Error error={error} />

      <p className="ayuda">
        Al guardarla, el pago pasa a la bandeja de Contabilidad por el
        importe de la factura. La orden es por {moneda(orden.total)}.
      </p>

      <div className="campo">
        <label htmlFor="folio-factura">Folio de la factura *</label>
        <input
          id="folio-factura" value={folio} maxLength={40}
          onChange={(e) => setFolio(e.target.value)}
        />
      </div>

      <div className="campo">
        <label htmlFor="fecha-factura">Fecha de la factura *</label>
        <input
          id="fecha-factura" type="date" value={fecha}
          onChange={(e) => setFecha(e.target.value)}
        />
        <span className="ayuda">
          La que trae impresa, no la de hoy: de ella salen los{' '}
          {orden.diasCredito ?? 0} días de crédito.
        </span>
      </div>

      <div className="campo">
        <label htmlFor="importe-factura">Importe *</label>
        <input
          id="importe-factura" type="number" min={0} step="0.01" value={importe}
          onChange={(e) => setImporte(e.target.value)}
        />
        {/* El descuadre se dice antes de guardar, que es cuando todavía
            se le puede reclamar al proveedor. No lo impide: un flete o
            un ajuste son legítimos. */}
        {diferencia !== 0 && cuanto > 0 && (
          <span className="etiqueta no" style={{ marginTop: 4 }}>
            {diferencia > 0
              ? `Cobra ${moneda(diferencia)} más que la orden.`
              : `Cobra ${moneda(-diferencia)} menos que la orden.`}
          </span>
        )}
      </div>

      <div className="campo">
        <label htmlFor="uuid-factura">UUID fiscal</label>
        <input
          id="uuid-factura" value={uuidFiscal} maxLength={36}
          onChange={(e) => setUuidFiscal(e.target.value)}
        />
        <span className="ayuda">
          Opcional: viene en el XML. Con él, el sistema impide que la misma
          factura se capture dos veces.
        </span>
      </div>

      <div className="campo">
        <label htmlFor="archivo-factura">El archivo *</label>
        <input
          id="archivo-factura" type="file" accept="application/pdf,image/*"
          onChange={(e) => setArchivo(e.target.files?.[0] ?? null)}
        />
        <span className="ayuda">PDF o foto, hasta 10 MB.</span>
      </div>

      <Faltantes que={faltantes} />
    </Dialogo>
  );
}

export function Recepcion({
  orden, onCerrar, onHecho,
}: {
  orden: OrdenCompraDetalle; onCerrar: () => void; onHecho: () => void;
}) {
  const [bodegas, setBodegas] = useState<Opcion[]>([]);
  const [bodegaId, setBodegaId] = useState('');
  const [cantidades, setCantidades] = useState<Record<string, string>>({});
  const [remision, setRemision] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  useEffect(() => {
    api.get<Opcion[]>('/catalogos/bodegas/opciones')
      .then((lista) => { setBodegas(lista); if (lista[0]) setBodegaId(lista[0].id); })
      .catch(() => undefined);
  }, []);

  const pendientes = orden.partidas.filter((p) => p.pendiente > 0);
  const hayAlgo = Object.values(cantidades).some((v) => Number(v) > 0);

  async function confirmar() {
    setError(null);
    setTrabajando(true);
    try {
      await api.post(`/ordenes-compra/${orden.id}/recepciones`, {
        bodegaId,
        remision: remision.trim() || undefined,
        observaciones: observaciones.trim() || undefined,
        partidas: pendientes
          .filter((p) => Number(cantidades[p.id]) > 0)
          .map((p) => ({ partidaId: p.id, cantidad: Number(cantidades[p.id]) })),
      });
      onHecho();
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  return (
    <Dialogo
      titulo={`Recepción de ${orden.folio}`}
      onCerrar={onCerrar}
      pie={
        <>
          <button onClick={onCerrar} disabled={trabajando}>Cancelar</button>
          <button
            className="primario"
            onClick={confirmar}
            disabled={trabajando || !bodegaId || !hayAlgo}
          >
            {trabajando ? 'Registrando…' : 'Registrar entrada'}
          </button>
        </>
      }
    >
      <Error error={error} />

      <div className="aviso">
        Lo que registres aquí entra al inventario valuado al precio de esta
        orden, y con eso se recalcula el costo promedio de cada pieza.
      </div>

      {bodegas.length > 1 && (
        <div className="campo">
          <label htmlFor="bodegaId">Bodega *</label>
          <select id="bodegaId" value={bodegaId} onChange={(e) => setBodegaId(e.target.value)}>
            {bodegas.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
          </select>
        </div>
      )}

      <table>
        <thead>
          <tr>
            <th>Refacción</th>
            <th className="numero">Falta</th>
            <th className="numero">Llega ahora</th>
          </tr>
        </thead>
        <tbody>
          {pendientes.map((p) => (
            <tr key={p.id}>
              <td>
                {p.refaccion
                  ? <><span className="mono">{p.refaccion.codigo}</span> {p.refaccion.nombre}</>
                  : p.descripcionLibre}
              </td>
              <td className="numero">{p.pendiente}</td>
              <td className="numero">
                <input
                  type="number" min={0} max={p.pendiente} step={1}
                  style={{ width: 90 }}
                  value={cantidades[p.id] ?? ''}
                  onChange={(e) => setCantidades({ ...cantidades, [p.id]: e.target.value })}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="campo">
        <label htmlFor="remision">Remisión o factura</label>
        <input
          id="remision" className="mono" value={remision} maxLength={30}
          onChange={(e) => setRemision(e.target.value)}
        />
        <span className="ayuda">Queda como referencia del movimiento de inventario.</span>
      </div>

      <div className="campo">
        <label htmlFor="observaciones">Observaciones</label>
        <textarea
          id="observaciones" rows={2} value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)}
        />
      </div>
    </Dialogo>
  );
}

// ══════════════════════════════════════════════════ alta de la orden

function FormularioOrden({
  onCerrar, onCreada, inicial,
}: {
  onCerrar: () => void; onCreada: (id: string) => void;
  /** Solicitud que ya viene elegida, si la compra nació de una. */
  inicial?: SolicitudCompra | null;
}) {
  const [proveedores, setProveedores] = useState<Opcion[]>([]);
  const [proveedorId, setProveedorId] = useState('');
  // El caso normal es que cause IVA; hay compras exentas y proveedores
  // que facturan sin desglosarlo.
  const [retencion, setRetencion] = useState(false);
  const [tipo, setTipo] = useState<'ot' | 'abasto'>('abasto');
  const [prioridad, setPrioridad] =
    useState<'baja' | 'media' | 'alta' | 'urgente'>('media');
  const [fechaEntrega, setFechaEntrega] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [partidas, setPartidas] = useState<PartidaBorrador[]>([]);
  const [fallaProveedores, setFallaProveedores] = useState<string>();
  const [fallaPendientes, setFallaPendientes] = useState<string>();
  const [busqueda, setBusqueda] = useState('');
  const [refacciones, setRefacciones] = useState<Refaccion[]>([]);
  // Lo que alguien pidió y todavía nadie ha comprado. Es de donde debería
  // salir casi toda orden de compra.
  const [pendientes, setPendientes] = useState<SolicitudCompra[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  useEffect(() => {
    api.get<Pagina<{ id: string; nombre: string }>>('/proveedores', {
      estado: 'activo', limite: 200,
    })
      .then((r) => {
        setProveedores(r.data);
        setFallaProveedores(r.data.length > 0 ? undefined
          : 'No hay proveedores activos dados de alta.');
      })
      // Sin proveedor no se puede guardar la orden, y el desplegable
      // vacío no dice si es que no hay ninguno o si faltó el permiso.
      .catch((e: unknown) => setFallaProveedores(motivoDeFalla(e)));

    api.get<Pagina<SolicitudCompra>>('/solicitudes-compra', {
      estatus: 'creada', limite: 100,
    })
      .then((r) => setPendientes(r.data))
      // Si esto falla, la tabla de lo que alguien pidió desaparece
      // entera y se acaba tecleando la pieza a mano, que es justo lo que
      // esa tabla existe para evitar.
      .catch((e: unknown) => setFallaPendientes(motivoDeFalla(e)));
  }, []);

  /**
   * Agrega una solicitud como partida, arrastrando su vínculo.
   *
   * Es lo que hace que, al recibir la mercancía, la pieza vuelva sola a la
   * solicitud de almacén que la esperaba y de ahí a su orden de trabajo.
   * Tecleando la pieza a mano la compra llega igual, pero se queda huérfana
   * y nadie se entera de que ya está en el anaquel.
   */
  const agregarSolicitud = useCallback((solicitud: SolicitudCompra) => {
    if (!solicitud.refaccion) return;

    setPartidas((antes) => {
      if (antes.some((p) => p.solicitudCompraId === solicitud.id)) return antes;
      return [...antes, {
        refaccionId: solicitud.refaccion!.id,
        solicitudCompraId: solicitud.id,
        ordenTrabajoId: solicitud.ordenTrabajo?.id,
        etiqueta: `${solicitud.refaccion!.codigo} — ${solicitud.refaccion!.nombre}`,
        cantidad: String(solicitud.cantidad),
        // El costo promedio del inventario como propuesta. Cero se
        // guardaba tal cual y, si la pieza nunca había entrado, dejaba el
        // costo promedio en cero para siempre: a partir de ahí toda
        // salida a una orden le cargaba cero de refacciones y el margen
        // que ve Operaciones era inventado.
        montoCompra: solicitud.refaccion?.costo_promedio ?? '0',
        montoVenta: '',
      }];
    });

    if (solicitud.ordenTrabajo) setTipo('ot');
  }, []);

  // Si la compra nació de una solicitud, esa pieza ya viene puesta.
  useEffect(() => {
    if (inicial) agregarSolicitud(inicial);
  }, [inicial, agregarSolicitud]);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get<Pagina<Refaccion>>('/refacciones', {
        busqueda: busqueda || undefined, estado: 'activo', limite: 15,
      })
        .then((r) => setRefacciones(r.data))
        .catch(() => setRefacciones([]));
    }, busqueda ? 300 : 0);
    return () => clearTimeout(t);
  }, [busqueda]);

  function agregar(refaccion: Refaccion) {
    if (partidas.some((p) => p.refaccionId === refaccion.id)) return;
    setPartidas([...partidas, {
      refaccionId: refaccion.id,
      etiqueta: `${refaccion.codigo} — ${refaccion.nombre}`,
      cantidad: '1',
      montoCompra: refaccion.costoPromedio ?? '0',
      montoVenta: refaccion.precioVentaSugerido ?? '',
    }]);
  }

  const subtotal = partidas.reduce(
    (suma, p) => suma + (Number(p.cantidad) || 0) * (Number(p.montoCompra) || 0),
    0,
  );

  /**
   * De qué orden de trabajo es esta compra.
   *
   * La marca la primera partida que traiga una, y a partir de ahí la
   * compra es de esa orden y de ninguna otra. Antes se calculaba al revés
   * —se juntaban todas las órdenes de las partidas y si salían dos, la
   * compra dejaba de ser de ninguna y pasaba a abasto—, y eso ocurría en
   * silencio: se agregaba una solicitud de otra OT y la compra perdía su
   * vínculo sin que nada lo dijera.
   *
   * Cuando la compra es de una orden se compra lo que se pidió desde
   * ella y nada más: alguien revisó esa lista y autorizó ese gasto. Una
   * pieza que entre por otro lado llega cargada a esa OT, con su costo,
   * sin haber pasado por la solicitud que la justifica.
   *
   * Para lo demás está la compra de abasto, que es justo para eso.
   */
  const ordenTrabajoId = partidas.find((p) => p.ordenTrabajoId)?.ordenTrabajoId;
  const vieneDeOt = Boolean(ordenTrabajoId);
  const tipoFinal: 'ot' | 'abasto' = ordenTrabajoId ? 'ot' : tipo;

  /**
   * Lo que se puede agregar: sólo lo pedido desde esta misma orden.
   *
   * Karin: «por que al generar ordenes de compra que vienen de una OT me
   * sigue permitiendo agregar mas refacciones?». Por esta tabla, que las
   * enseñaba todas —de otras órdenes y de abasto— aunque la caja de
   * agregar piezas sueltas sí estuviera escondida.
   *
   * Mientras la compra no sea de ninguna orden se ven todas, porque
   * cualquiera puede ser la primera.
   */
  const seleccionables = ordenTrabajoId
    ? pendientes.filter((s) => s.ordenTrabajo?.id === ordenTrabajoId)
    : pendientes;

  /** Lo que queda fuera por ser de otra orden, para poder decirlo. */
  const deOtrasOrdenes = pendientes.length - seleccionables.length;

  async function guardar() {
    setError(null);
    setTrabajando(true);
    try {
      const r = await api.post<{ id: string; folio: string }>('/ordenes-compra', {
        proveedorId,
        tipo: tipoFinal,
        prioridad,
        ordenTrabajoId,
        retencion,
        fechaEntregaEstimada: fechaEntrega ? new Date(fechaEntrega).toISOString() : undefined,
        observaciones: observaciones.trim() || undefined,
        partidas: partidas.map((p) => ({
          refaccionId: p.refaccionId,
          solicitudCompraId: p.solicitudCompraId,
          cantidad: Number(p.cantidad),
          montoCompra: Number(p.montoCompra),
          montoVenta: p.montoVenta === '' ? undefined : Number(p.montoVenta),
        })),
      });
      onCreada(r.id);
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  return (
    <Dialogo
      titulo="Nueva orden de compra"
      onCerrar={onCerrar}
      pie={
        <>
          <button onClick={onCerrar} disabled={trabajando}>Cancelar</button>
          <button
            className="primario"
            onClick={guardar}
            disabled={trabajando || !proveedorId || partidas.length === 0}
          >
            {trabajando ? 'Creando…' : 'Crear en borrador'}
          </button>
        </>
      }
    >
      <Error error={error} />

      <div className="campo">
        <label htmlFor="proveedorId">Proveedor *</label>
        <select
          id="proveedorId" value={proveedorId}
          onChange={(e) => setProveedorId(e.target.value)}
        >
          <option value="">— elige un proveedor —</option>
          {proveedores.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
        </select>
        {/* Sin proveedor no se guarda la orden, y un desplegable vacío no
            dice si es que no hay ninguno o si faltó el permiso. */}
        <Falla mensaje={fallaProveedores} />
      </div>

      <div className="campo">
        <label htmlFor="tipo">Tipo</label>
        <select id="tipo" value={tipo} onChange={(e) => setTipo(e.target.value as 'ot' | 'abasto')}>
          <option value="abasto">Abasto de almacén</option>
          <option value="ot">Para una orden de trabajo</option>
        </select>
      </div>

      <div className="campo">
        <label htmlFor="prioridad">Prioridad</label>
        <select
          id="prioridad" value={prioridad}
          onChange={(e) => setPrioridad(e.target.value as typeof prioridad)}
        >
          <option value="baja">Baja</option>
          <option value="media">Media</option>
          <option value="alta">Alta</option>
          <option value="urgente">Urgente</option>
        </select>
        <span className="ayuda">
          Ordena las bandejas de quien autoriza y de contabilidad. Con
          veinte órdenes enfrente, es lo que dice cuál corre.
        </span>
      </div>

      <div className="campo">
        <label htmlFor="retencion">
          <input
            id="retencion" type="checkbox" checked={retencion}
            onChange={(e) => setRetencion(e.target.checked)}
          />
          {' '}A este proveedor se le retiene
        </label>
        <span className="ayuda">
          Aplica el 1.25% a todas las partidas. Se retiene: baja lo que se le
          paga al proveedor, y APRISA entera esa parte al SAT. El IVA va
          siempre, con la tasa configurada en la empresa.
        </span>
      </div>

      <div className="campo">
        <label htmlFor="fechaEntrega">Entrega estimada</label>
        <input
          id="fechaEntrega" type="date" value={fechaEntrega}
          onChange={(e) => setFechaEntrega(e.target.value)}
        />
      </div>

      <h2>Partidas</h2>

      {/* Primero lo pedido, después el catálogo. Casi toda orden de compra
          debería salir de una solicitud: es lo que hace que la pieza vuelva
          sola a la orden de trabajo que la espera cuando la mercancía
          llegue. Tecleada a mano llega igual, pero se queda huérfana. */}
      <Falla mensaje={fallaPendientes} />

      {seleccionables.length > 0 && (
        <div className="campo">
          <label>
            {vieneDeOt ? 'Lo que se pidió para esta orden' : 'Solicitudes sin atender'}
          </label>
          <table>
            <thead>
              <tr>
                <th>Folio</th><th>Pieza</th><th>Orden</th>
                <th className="numero">Cantidad</th><th />
              </tr>
            </thead>
            <tbody>
              {seleccionables.map((s) => {
                const puesta = partidas.some((p) => p.solicitudCompraId === s.id);
                return (
                  <tr key={s.id}>
                    <td className="mono">{s.folio}</td>
                    <td>
                      {s.refaccion
                        ? <><span className="mono">{s.refaccion.codigo}</span> — {s.refaccion.nombre}</>
                        : <span className="conteo">{s.descripcionLibre}</span>}
                    </td>
                    <td className="mono">{s.ordenTrabajo ? folioOT(s.ordenTrabajo.folio) : '—'}</td>
                    <td className="numero">{s.cantidad}</td>
                    <td>
                      {puesta
                        ? <span className="etiqueta si">Agregada</span>
                        : s.refaccion
                          ? <button className="enlace" onClick={() => agregarSolicitud(s)}>
                              Agregar
                            </button>
                          // Sin refacción del catálogo no puede entrar al
                          // inventario al recibirse; hay que darla de alta
                          // antes, y decirlo aquí evita descubrirlo al final.
                          : <span className="ayuda">Falta darla de alta</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {vieneDeOt ? (
        <div className="aviso">
          Esta compra es para una orden de trabajo, así que sólo lleva lo
          que se pidió desde ella. Si hace falta otra pieza para esa orden,
          se solicita desde la orden; si es para el anaquel, va en una
          compra de abasto.
          {deOtrasOrdenes > 0 && (
            <>
              {' '}Hay {deOtrasOrdenes}{' '}
              {deOtrasOrdenes === 1 ? 'solicitud pendiente' : 'solicitudes pendientes'}
              {' '}de otras órdenes que no se enseñan aquí: al recibirse, la
              mercancía vuelve a la orden que la espera, y con dos órdenes
              en la misma compra no se sabría a cuál.
            </>
          )}
        </div>
      ) : (
        <div className="campo">
          <label htmlFor="buscarRefaccion">Agregar refacción suelta</label>
          <input
            id="buscarRefaccion" type="search" value={busqueda}
            placeholder="Código, número de parte o nombre…"
            onChange={(e) => setBusqueda(e.target.value)}
          />
          <div style={{ display: 'grid', gap: 4, maxHeight: 140, overflow: 'auto' }}>
            {refacciones.map((r) => (
              <button key={r.id} className="enlace" style={{ textAlign: 'left' }}
                      onClick={() => agregar(r)}>
                + <span className="mono">{r.codigo}</span> {r.nombre}
              </button>
            ))}
          </div>
        </div>
      )}

      {partidas.length === 0 ? (
        <div className="vacio">Agrega al menos una refacción.</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Refacción</th>
              <th className="numero">Cantidad</th>
              <th className="numero">Costo</th>
              <th className="numero">Venta</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {/* La clave lleva la solicitud además de la refacción: dos
                órdenes de trabajo distintas pueden pedir la misma pieza,
                y con la sola refacción las dos filas compartían clave.
                React reutilizaba los nodos y los campos de cantidad y
                costo se comportaban de forma errática al editarlos. */}
            {partidas.map((p, i) => (
              <tr key={`${p.refaccionId}-${p.solicitudCompraId ?? 'suelta'}`}>
                <td>{p.etiqueta}</td>
                <td className="numero">
                  <input
                    type="number" min={1} step={1} style={{ width: 80 }} value={p.cantidad}
                    onChange={(e) => cambiar(i, { cantidad: e.target.value })}
                  />
                </td>
                <td className="numero">
                  <input
                    type="number" min={0} step="0.01" style={{ width: 100 }} value={p.montoCompra}
                    onChange={(e) => cambiar(i, { montoCompra: e.target.value })}
                  />
                </td>
                <td className="numero">
                  <input
                    type="number" min={0} step="0.01" style={{ width: 100 }} value={p.montoVenta}
                    onChange={(e) => cambiar(i, { montoVenta: e.target.value })}
                  />
                </td>
                <td className="acciones">
                  <button className="enlace"
                          onClick={() => setPartidas(partidas.filter((_, j) => j !== i))}>
                    Quitar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {partidas.length > 0 && (
        <div className="aviso">
          Subtotal estimado: <strong>{moneda(subtotal)}</strong>. El IVA y el
          total los calcula el servidor al guardar.
        </div>
      )}

      <div className="campo">
        <label htmlFor="observaciones">Observaciones</label>
        <textarea
          id="observaciones" rows={2} value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)}
        />
      </div>
    </Dialogo>
  );

  function cambiar(indice: number, cambios: Partial<PartidaBorrador>) {
    setPartidas(partidas.map((p, i) => (i === indice ? { ...p, ...cambios } : p)));
  }
}

interface PartidaBorrador {
  refaccionId: string;
  /** De qué solicitud sale. Es lo que hace que la pieza vuelva a su orden. */
  solicitudCompraId?: string;
  /** Para qué orden de trabajo se pidió, si se pidió para alguna. */
  ordenTrabajoId?: string;
  etiqueta: string;
  cantidad: string;
  montoCompra: string;
  montoVenta: string;
}

// ══════════════════════════════════════════════════════ solicitudes

function Solicitudes({
  puede, recarga, alCambiar, alComprar,
}: {
  puede: Puede; recarga: number; alCambiar: () => void;
  /** Arma una orden de compra con esta solicitud ya dentro. */
  alComprar: (solicitud: SolicitudCompra) => void;
}) {
  const [estatus, setEstatus] = useState('creada');
  const [busqueda, setBusqueda] = useState('');
  const [numero, setNumero] = useState(1);
  const [pagina, setPagina] = useState<Pagina<SolicitudCompra> | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [creando, setCreando] = useState(false);
  /**
   * La solicitud que se está cancelando, y el motivo.
   *
   * La API tenía el endpoint desde siempre y la pantalla no tenía el
   * botón, así que una solicitud que ya no procede se quedaba de por vida
   * en «pendientes», contando en el número del menú. Se nota cuando se
   * cancela la orden de compra que la atendía: la solicitud vuelve a
   * pendiente y no había manera de cerrarla.
   */
  const [cancelando, setCancelando] = useState<SolicitudCompra | null>(null);
  const [motivo, setMotivo] = useState('');
  const [trabajando, setTrabajando] = useState(false);
  const [fallaCancelar, setFallaCancelar] = useState<unknown>(null);

  const cargar = useCallback(() => {
    setCargando(true);
    setError(null);
    api.get<Pagina<SolicitudCompra>>('/solicitudes-compra', {
      estatus: estatus || undefined,
      busqueda: busqueda || undefined,
      pagina: numero,
      limite: 50,
    })
      .then(setPagina)
      .catch(setError)
      .finally(() => setCargando(false));
  }, [estatus, busqueda, numero, recarga]);

  useEffect(() => {
    const t = setTimeout(cargar, busqueda ? 300 : 0);
    return () => clearTimeout(t);
  }, [cargar, busqueda]);

  return (
    <div className="panel">
      <div className="controles">
        <select value={estatus} onChange={(e) => { setEstatus(e.target.value); setNumero(1); }}>
          <option value="creada">Pendientes</option>
          <option value="en_oc">En orden de compra</option>
          <option value="atendida">Atendidas</option>
          <option value="cancelada">Canceladas</option>
          <option value="">Todas</option>
        </select>
        <input
          type="search"
          placeholder="Folio o refacción…"
          value={busqueda}
          onChange={(e) => { setBusqueda(e.target.value); setNumero(1); }}
        />
        {puede('almacen', 'crear') && (
          <button className="primario" onClick={() => setCreando(true)}>Nueva solicitud</button>
        )}
      </div>

      <Error error={error} />

      {cargando && !pagina ? <Cargando /> : pagina && (
        <>
          {pagina.data.length === 0 ? (
            <div className="vacio">No hay solicitudes con ese filtro.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Folio</th>
                  <th>Qué se pide</th>
                  <th className="numero">Cantidad</th>
                  <th>Orden</th>
                  <th>Justificación</th>
                  <th>Estatus</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {pagina.data.map((s) => (
                  <tr key={s.id}>
                    <td className="mono">{s.folio}</td>
                    <td>
                      {s.refaccion
                        ? <><span className="mono">{s.refaccion.codigo}</span> {s.refaccion.nombre}</>
                        : s.descripcionLibre}
                    </td>
                    <td className="numero">{s.cantidad}</td>
                    <td className="mono">{s.ordenTrabajo ? folioOT(s.ordenTrabajo.folio) : '—'}</td>
                    <td style={{ maxWidth: 260 }}>{s.justificacion ?? '—'}</td>
                    <td><span className="etiqueta">{ETIQUETAS[s.estatus] ?? s.estatus}</span></td>
                    <td>
                      {/* Atender una solicitud es comprarla. El botón lleva a
                          la orden de compra con la pieza ya puesta, que es lo
                          que conserva el vínculo de vuelta a su orden de
                          trabajo. Sin él habría que copiar el folio y volver
                          a buscar la pieza a mano, y así fue como la primera
                          compra salió sin ligar a nada. */}
                      {s.estatus === 'creada' && puede('compras', 'crear') && (
                        s.refaccion
                          ? (
                            <button className="primario" onClick={() => alComprar(s)}>
                              Comprar
                            </button>
                          )
                          // Sin refacción de catálogo no hay qué dar de alta en
                          // el inventario cuando llegue: primero se cataloga.
                          : <span className="ayuda">Falta darla de alta en el catálogo</span>
                      )}
                      {/* Y la salida, para la que ya no procede: la orden se
                          cerró, la pieza se consiguió por otro lado, se pidió
                          por error. Sin esto se quedaba pendiente para
                          siempre. No se borra: se cancela con su motivo. */}
                      {s.estatus === 'creada' && puede('compras', 'editar') && (
                        <button
                          className="enlace"
                          onClick={() => { setMotivo(''); setFallaCancelar(null); setCancelando(s); }}
                        >
                          Cancelar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <Paginacion
            pagina={pagina.pagina} limite={pagina.limite} total={pagina.total}
            onCambiar={setNumero}
          />
        </>
      )}

      {creando && (
        <FormularioSolicitud
          onCerrar={() => setCreando(false)}
          onCreada={() => { setCreando(false); cargar(); alCambiar(); }}
        />
      )}

      {cancelando && (
        <DialogoMotivo
          titulo={`Cancelar la solicitud ${cancelando.folio}`}
          descripcion={
            cancelando.ordenTrabajo
              ? `Es de la orden ${folioOT(cancelando.ordenTrabajo.folio)}. Di por qué`
                + ' ya no procede: la orden se cerró, la pieza se consiguió por'
                + ' otro lado, se pidió por error. La solicitud no se borra, queda'
                + ' cancelada con tu motivo.'
              : 'Di por qué ya no procede. La solicitud no se borra: queda'
                + ' cancelada con tu motivo.'
          }
          textoBoton="Cancelar la solicitud"
          motivo={motivo}
          setMotivo={setMotivo}
          trabajando={trabajando}
          error={fallaCancelar}
          onCerrar={() => setCancelando(null)}
          onConfirmar={() => {
            setFallaCancelar(null);
            setTrabajando(true);
            api.post(`/solicitudes-compra/${cancelando.id}/cancelar`, { motivo })
              .then(() => { setCancelando(null); cargar(); alCambiar(); })
              .catch(setFallaCancelar)
              .finally(() => setTrabajando(false));
          }}
        />
      )}
    </div>
  );
}

function FormularioSolicitud({
  onCerrar, onCreada,
}: {
  onCerrar: () => void; onCreada: () => void;
}) {
  const [busqueda, setBusqueda] = useState('');
  const [refacciones, setRefacciones] = useState<Refaccion[]>([]);
  const [refaccionId, setRefaccionId] = useState('');
  const [descripcionLibre, setDescripcionLibre] = useState('');
  const [cantidad, setCantidad] = useState('1');
  const [justificacion, setJustificacion] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get<Pagina<Refaccion>>('/refacciones', {
        busqueda: busqueda || undefined, estado: 'activo', limite: 15,
      })
        .then((r) => setRefacciones(r.data))
        .catch(() => setRefacciones([]));
    }, busqueda ? 300 : 0);
    return () => clearTimeout(t);
  }, [busqueda]);

  const listo = (Boolean(refaccionId) || descripcionLibre.trim().length > 0)
    && justificacion.trim().length >= 10;

  async function guardar() {
    setError(null);
    setTrabajando(true);
    try {
      await api.post('/solicitudes-compra', {
        refaccionId: refaccionId || undefined,
        descripcionLibre: descripcionLibre.trim() || undefined,
        cantidad: Number(cantidad),
        justificacion: justificacion.trim(),
      });
      onCreada();
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  return (
    <Dialogo
      titulo="Nueva solicitud de compra"
      onCerrar={onCerrar}
      pie={
        <>
          <button onClick={onCerrar} disabled={trabajando}>Cancelar</button>
          <button className="primario" onClick={guardar} disabled={trabajando || !listo}>
            {trabajando ? 'Guardando…' : 'Solicitar'}
          </button>
        </>
      }
    >
      <Error error={error} />

      <div className="campo">
        <label htmlFor="buscarRefaccion">Refacción del catálogo</label>
        <input
          id="buscarRefaccion" type="search" value={busqueda}
          placeholder="Código, número de parte o nombre…"
          onChange={(e) => setBusqueda(e.target.value)}
        />
        <select value={refaccionId} size={5} onChange={(e) => setRefaccionId(e.target.value)}>
          <option value="">— no está en el catálogo —</option>
          {refacciones.map((r) => (
            <option key={r.id} value={r.id}>{r.codigo} — {r.nombre}</option>
          ))}
        </select>
      </div>

      {!refaccionId && (
        <div className="campo">
          <label htmlFor="descripcionLibre">Descripción de lo que se necesita</label>
          <input
            id="descripcionLibre" value={descripcionLibre} maxLength={250}
            onChange={(e) => setDescripcionLibre(e.target.value)}
          />
          <span className="ayuda">
            Para piezas que todavía no están dadas de alta. Compras la
            captura al hacer la orden.
          </span>
        </div>
      )}

      <div className="campo">
        <label htmlFor="cantidad">Cantidad *</label>
        <input
          id="cantidad" type="number" min={1} step={1} value={cantidad}
          onChange={(e) => setCantidad(e.target.value)}
        />
      </div>

      <div className="campo">
        <label htmlFor="justificacion">Para qué se necesita *</label>
        <textarea
          id="justificacion" rows={3} value={justificacion}
          onChange={(e) => setJustificacion(e.target.value)}
        />
        <span className="ayuda">
          Mínimo 10 caracteres. Es lo que lee quien autoriza el gasto.
        </span>
      </div>
    </Dialogo>
  );
}

function Dato({ etiqueta, valor }: { etiqueta: string; valor: string | null }) {
  return (
    <div className="campo">
      <label>{etiqueta}</label>
      <span>{valor ?? '—'}</span>
    </div>
  );
}
